import {
    Box,
    Grid2,
    TextField,
    Tooltip,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Typography,
} from '@mui/material';
import CommonButton from '../../../components/atom/CommonButton';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguages } from '../../../i18n';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import useNotify from '../../../hooks/useNotify';
import { editBooking } from '@openapi/bookings/bookings';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { BOOKING_STATUSES, INTEGRATION_DISPLAY_NAMES } from '../../../helpers/constants';
import BlockWithTitle from '../../../components/atom/BlockWithTitle';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';

function EditBookingDetails({
    bookingId,
    booking,
    reloadData,
    readOnly,
    contact,
    onContactChange,
    emailError,
}) {
    const { can, CAPABILITIES } = useCapabilities();
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const { showModal } = useModals();
    const [isTogglingStillBillable, setIsTogglingStillBillable] = useState(false);
    const [reasonDraft, setReasonDraft] = useState(null);

    const channels = contact.notificationChannels ?? ['email'];
    const setChannel = (channel, enabled) => {
        onContactChange(
            'notificationChannels',
            enabled ? [...new Set([...channels, channel])] : channels.filter((c) => c !== channel),
        );
    };

    const cancelWindow = booking.timeslot?.cancelWindow;
    const startTime = booking.timeslot?.startTime;
    const cancelTime = startTime && cancelWindow > 0 ? new Date(startTime) : null;
    if (cancelTime) cancelTime.setHours(cancelTime.getHours() - cancelWindow);
    const isPastCancelWindow = !!cancelTime && new Date() >= cancelTime;

    const canEditStillBillable =
        !readOnly &&
        can(CAPABILITIES.INVOICES_VIEW) &&
        booking.status === BOOKING_STATUSES.CANCELLED &&
        !!booking.agentId &&
        !booking.integrationSource &&
        !!booking.agentInvoiceable &&
        (booking.stillBillable || isPastCancelWindow);

    const submitStillBillable = async (next, reason) => {
        setIsTogglingStillBillable(true);
        try {
            await editBooking(Number(bookingId), {
                status: booking.status,
                venueId: booking.venueId,
                timeslotId: booking.timeslotId,
                firstName: booking.firstName,
                lastName: booking.lastName,
                phone: booking.phone,
                email: booking.email,
                language: booking.language,
                note: booking.note,
                checkinNote: booking.checkinNote,
                isReviewEmailSuppressed: booking.isReviewEmailSuppressed,
                notificationChannels: booking.notificationChannels ?? ['email'],
                agentId: booking.agentId,
                stillBillable: next,
                stillBillableReason: next ? reason || null : null,
            });
            reloadData();
            notifySuccess(t('bookings.notifications.updated'));
        } catch (err) {
            notifyError(err);
        } finally {
            setIsTogglingStillBillable(false);
            setReasonDraft(null);
        }
    };

    const onStillBillableCheckboxChange = (checked) => {
        if (checked) {
            setReasonDraft(booking.stillBillableReason || '');
        } else {
            submitStillBillable(false, null);
        }
    };

    return (
        <BlockWithTitle title={t('bookings.bookingContacts')}>
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        disabled={readOnly}
                        label={t('bookings.columns.firstName')}
                        value={contact.firstName}
                        onChange={(ev) => onContactChange('firstName', ev.target.value)}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        disabled={readOnly}
                        label={t('bookings.columns.lastName')}
                        value={contact.lastName}
                        onChange={(ev) => onContactChange('lastName', ev.target.value)}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        disabled={readOnly}
                        label={t('bookings.columns.phone')}
                        value={contact.phone}
                        inputProps={{ maxLength: 20, inputMode: 'tel' }}
                        onChange={(ev) => {
                            const cleaned = ev.target.value
                                .replace(/[^\d+\-() ]/g, '')
                                .slice(0, 20);
                            onContactChange('phone', cleaned);
                        }}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        disabled={readOnly}
                        label={t('bookings.columns.email')}
                        value={contact.email}
                        onChange={(ev) => onContactChange('email', ev.target.value)}
                        error={emailError}
                        helperText={emailError ? t('errors.email') : ''}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <FormControl fullWidth>
                        <InputLabel>{t('bookings.columns.language')}</InputLabel>
                        <Select
                            sx={{
                                '& .MuiSelect-select': {
                                    textAlign: 'left',
                                    paddingLeft: 0,
                                },
                            }}
                            disabled={readOnly}
                            value={contact.language || ''}
                            label={t('bookings.columns.language')}
                            onChange={(ev) => onContactChange('language', ev.target.value)}
                        >
                            <MenuItem value="">
                                <em>{t('common.none')}</em>
                            </MenuItem>
                            {getLanguages().map((lang) => (
                                <MenuItem key={lang} value={lang}>
                                    {lang.toUpperCase()}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            columnGap: 3,
                            textAlign: 'left',
                        }}
                    >
                        <Tooltip
                            title={
                                !booking.timeslot?.showNotificationChannels?.includes('email')
                                    ? t(
                                          'bookings.columns.channelNotAvailableOnShow',
                                          'Email is not available on this show',
                                      )
                                    : ''
                            }
                        >
                            <span>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={channels.includes('email')}
                                            onChange={(ev) =>
                                                setChannel('email', ev.target.checked)
                                            }
                                            disabled={
                                                readOnly ||
                                                !booking.timeslot?.showNotificationChannels?.includes(
                                                    'email',
                                                )
                                            }
                                        />
                                    }
                                    label={t('bookings.columns.emailNotifications')}
                                />
                            </span>
                        </Tooltip>
                        <Tooltip
                            title={
                                !booking.timeslot?.showNotificationChannels?.includes('sms')
                                    ? t(
                                          'bookings.columns.channelNotAvailableOnShow',
                                          'SMS is not available on this show',
                                      )
                                    : ''
                            }
                        >
                            <span>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={channels.includes('sms')}
                                            onChange={(ev) => setChannel('sms', ev.target.checked)}
                                            disabled={
                                                readOnly ||
                                                !booking.timeslot?.showNotificationChannels?.includes(
                                                    'sms',
                                                )
                                            }
                                        />
                                    }
                                    label={t('bookings.columns.smsNotifications')}
                                />
                            </span>
                        </Tooltip>
                    </Box>
                </Grid2>
                <Grid2
                    size={{ xs: 12 }}
                    sx={{ display: 'flex', justifyContent: 'flex-start', textAlign: 'left' }}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={Boolean(contact.isReviewEmailSuppressed)}
                                onChange={(ev) =>
                                    onContactChange('isReviewEmailSuppressed', ev.target.checked)
                                }
                                disabled={readOnly}
                            />
                        }
                        label={t('bookings.columns.isReviewEmailSuppressed')}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        multiline
                        disabled={readOnly}
                        label={t('bookings.columns.checkinNote')}
                        value={contact.checkinNote}
                        onChange={(ev) => onContactChange('checkinNote', ev.target.value)}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label={t('bookings.columns.agentName')}
                        value={booking.agentName || ''}
                        disabled
                    />
                    {booking.agentCommission != null && (
                        <Box
                            sx={{
                                fontStyle: 'italic',
                                color: 'text.secondary',
                                mt: 0.5,
                                textAlign: 'left',
                            }}
                        >
                            {t('bookings.columns.commission')}:{' '}
                            {formatCentsToUsd(booking.agentCommission)}
                        </Box>
                    )}
                </Grid2>
                {(booking.source === 'online' || booking.integrationSource) && (
                    <Grid2 size={{ xs: 12 }} sx={{ textAlign: 'left' }}>
                        <b>{t('bookings.columns.source')}: </b>
                        <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                            {booking.source === 'online'
                                ? booking.commissionGroupName || booking.agentName
                                    ? `${
                                          booking.commissionGroupName || booking.agentName
                                      } / ${t('bookings.sourceOnline')}`
                                    : t('bookings.sourceOnline')
                                : INTEGRATION_DISPLAY_NAMES[booking.integrationSource] ||
                                  booking.integrationSource}
                        </Box>
                    </Grid2>
                )}
                {can(CAPABILITIES.BOOKINGS_RE_SOURCE) && (
                    <Grid2 size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <CommonButton
                            buttonType="secondary"
                            label={t('bookings.reSource.button')}
                            onClick={() =>
                                showModal(MODAL_TYPE.RE_SOURCE_BOOKING, {
                                    booking,
                                    onSuccess: reloadData,
                                })
                            }
                        />
                    </Grid2>
                )}
                {canEditStillBillable && (
                    <Grid2 size={{ xs: 12 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(booking.stillBillable)}
                                    disabled={isTogglingStillBillable}
                                    onChange={(ev) =>
                                        onStillBillableCheckboxChange(ev.target.checked)
                                    }
                                />
                            }
                            label={t('bookings.stillBillableLabel')}
                        />
                        {booking.stillBillable && booking.stillBillableReason && (
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    fontStyle: 'italic',
                                    ml: 4,
                                }}
                            >
                                {booking.stillBillableReason}
                            </Typography>
                        )}
                    </Grid2>
                )}
            </Grid2>
            <Dialog
                open={reasonDraft !== null}
                onClose={() => setReasonDraft(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{t('bookings.stillBillableLabel')}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {t('bookings.stillBillableReasonPrompt')}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        inputProps={{ maxLength: 200 }}
                        value={reasonDraft ?? ''}
                        onChange={(ev) => setReasonDraft(ev.target.value)}
                        placeholder={t('bookings.stillBillableReasonPlaceholder')}
                    />
                </DialogContent>
                <DialogActions>
                    <CommonButton
                        onClick={() => setReasonDraft(null)}
                        label={t('common.cancel')}
                        buttonType="secondary"
                        disabled={isTogglingStillBillable}
                    />
                    <CommonButton
                        onClick={() => submitStillBillable(true, (reasonDraft ?? '').trim())}
                        label={t('common.save')}
                        buttonType="primary"
                        disabled={
                            isTogglingStillBillable || (reasonDraft ?? '').trim().length === 0
                        }
                    />
                </DialogActions>
            </Dialog>
        </BlockWithTitle>
    );
}

export default EditBookingDetails;
